import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  GeometryCanvasPayload,
  GeometryCanvasRuntime,
} from '../../core/types/payloads/geometry_canvas.types';
import type {
  GeometryPayload,
  GeometryShapePart,
  GeometryShapeType,
  GeometryAnnotation,
  TriangleSubtype,
} from '../../core/types/mathGeometry';
import { normalizeTriangleSubtype } from '../../core/math/triangleGeometry';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';

const SHAPES = new Set<GeometryShapeType>(['rectangle', 'triangle', 'circle', 'compound', 'regular_polygon']);

function num(v: unknown): number | undefined {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function normalizeLabels(raw: unknown): GeometryPayload['labels'] {
  if (!raw || typeof raw !== 'object') return {};
  const d = raw as Record<string, unknown>;
  const pick = (k: string) => (d[k] != null ? String(d[k]) : undefined);
  return {
    top: pick('top'),
    right: pick('right'),
    bottom: pick('bottom'),
    left: pick('left'),
    center: pick('center'),
  };
}

function normalizeDimensions(raw: unknown): GeometryPayload['dimensions'] {
  if (!raw || typeof raw !== 'object') return {};
  const d = raw as Record<string, unknown>;
  return {
    width: num(d.width),
    height: num(d.height),
    radius: num(d.radius),
    base: num(d.base),
    sides: num(d.sides),
    sideLength: num(d.sideLength),
  };
}

function normalizeAnnotations(raw: unknown): GeometryAnnotation[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const annotations = raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const data = entry as Record<string, unknown>;
      const label = String(data.label || '').trim();
      if (!label) return null;
      const kind = data.kind === 'vertex' || data.kind === 'angle' ? data.kind : undefined;
      const at = data.at != null ? String(data.at).trim() : undefined;
      return { label, ...(kind ? { kind } : {}), ...(at ? { at } : {}) } as GeometryAnnotation;
    })
    .filter((entry): entry is GeometryAnnotation => Boolean(entry));
  return annotations.length ? annotations : undefined;
}

function normalizePart(raw: unknown): GeometryShapePart | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const shapeType = String(d.shapeType || '').trim() as GeometryShapePart['shapeType'];
  if (shapeType !== 'rectangle' && shapeType !== 'triangle' && shapeType !== 'circle') return null;
  const dimensions = normalizeDimensions(d.dimensions);
  if (shapeType === 'circle' && !dimensions.radius) return null;
  if (shapeType === 'triangle' && !(dimensions.base || dimensions.width) && !dimensions.height) {
    return null;
  }
  if (shapeType === 'rectangle' && (!dimensions.width || !dimensions.height)) return null;
  return {
    shapeType,
    dimensions,
    offsetX: num(d.offsetX) ?? 0,
    offsetY: num(d.offsetY) ?? 0,
    labels: normalizeLabels(d.labels),
    highlightEdges: Array.isArray(d.highlightEdges)
      ? (d.highlightEdges as GeometryShapePart['highlightEdges'])
      : undefined,
  };
}

export function normalizeGeometryPayload(raw: unknown): GeometryPayload {
  if (!raw || typeof raw !== 'object') {
    throw new Error('geometry requires shapeType and dimensions');
  }
  const data = raw as Record<string, unknown>;
  const shapeType = String(data.shapeType || 'rectangle').trim() as GeometryShapeType;
  if (!SHAPES.has(shapeType)) {
    throw new Error('geometry.shapeType invalid');
  }

  const dimensions = normalizeDimensions(data.dimensions);
  const labels = normalizeLabels(data.labels);
  const annotations = normalizeAnnotations(data.annotations);
  const parts = Array.isArray(data.parts)
    ? data.parts.map(normalizePart).filter((p): p is GeometryShapePart => Boolean(p))
    : undefined;

  if (shapeType === 'compound') {
    if (!parts?.length) {
      throw new Error('compound geometry requires parts[]');
    }
    return { shapeType, dimensions, labels, annotations, parts, unit: data.unit ? String(data.unit) : undefined };
  }

  if (shapeType === 'circle' && !dimensions.radius) {
    throw new Error('circle requires dimensions.radius');
  }
  if (shapeType === 'triangle') {
    const base = dimensions.base ?? dimensions.width;
    if (!base || !dimensions.height) {
      throw new Error('triangle requires base/width and height');
    }
  }
  if (shapeType === 'rectangle' && (!dimensions.width || !dimensions.height)) {
    throw new Error('rectangle requires width and height');
  }
  if (shapeType === 'regular_polygon') {
    const sides = Math.round(Number(dimensions.sides) || 0);
    if (sides < 3) throw new Error('regular_polygon requires dimensions.sides >= 3');
    if (!dimensions.sideLength) throw new Error('regular_polygon requires dimensions.sideLength');
  }

  const triangleSubtype =
    shapeType === 'triangle'
      ? normalizeTriangleSubtype(data.triangleSubtype)
      : undefined;

  return {
    shapeType,
    dimensions,
    labels,
    ...(triangleSubtype ? { triangleSubtype } : {}),
    annotations,
    highlightEdges: Array.isArray(data.highlightEdges)
      ? (data.highlightEdges as GeometryPayload['highlightEdges'])
      : undefined,
    unit: data.unit ? String(data.unit) : undefined,
  };
}

export function validateGeometryCanvasPayload(raw: unknown): GeometryCanvasPayload {
  if (!raw || typeof raw !== 'object') throw new Error('GeometryCanvas payload must be an object');
  const data = raw as Partial<GeometryCanvasPayload> & { geometry?: unknown };
  const title = String(data.title || '').trim();
  if (!title) throw new Error('GeometryCanvas requires title');
  const studentDraw = data.studentDraw === true;
  const geometry = studentDraw ? undefined : normalizeGeometryPayload(data.geometry);
  // (B) Guard: sin geometry ni studentDraw el canvas queda vacío — forzar reintento LLM / no factory title-only.
  if (!studentDraw && !geometry) {
    throw new Error(
      'GeometryCanvas requires geometry { shapeType, dimensions, labels } or studentDraw:true',
    );
  }
  return {
    title,
    bodyText: data.bodyText ? String(data.bodyText) : undefined,
    sectionTitle: data.sectionTitle ? String(data.sectionTitle) : undefined,
    prompt: data.prompt ? String(data.prompt) : undefined,
    ...(geometry ? { geometry } : {}),
    ...(studentDraw ? { studentDraw: true } : {}),
    formulaHint: data.formulaHint ? String(data.formulaHint) : undefined,
    teacherScript: data.teacherScript ? String(data.teacherScript) : undefined,
  };
}

export function createInitialGeometryCanvasRuntime(
  _payload: GeometryCanvasPayload,
  _routing: RoutingMode,
): GeometryCanvasRuntime {
  return { viewed: false };
}

export const geometryCanvasLogic: ActivityLogicPlugin<'geometry_canvas'> = {
  mode: 'geometry_canvas',
  validatePayload: validateGeometryCanvasPayload,
  createInitialRuntime: createInitialGeometryCanvasRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope) {
    if (input.action === 'EXAM_CONTINUE' || input.action === 'VIEW') {
      return { runtime: { ...ctx.runtime, viewed: true } };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoGeometryCanvasPayload(): GeometryCanvasPayload {
  // Geometría genérica — sin “quiosco del barrio” narrativo (demo de modo, no de tema).
  return validateGeometryCanvasPayload({
    title: 'Rectángulo de ejemplo',
    bodyText:
      'Observa la figura. Las medidas están etiquetadas. Usa la figura para calcular área y perímetro.',
    prompt: '¿Cuál es el área del rectángulo?',
    formulaHint: 'Área del rectángulo = base × altura',
    geometry: {
      shapeType: 'rectangle',
      dimensions: { width: 40, height: 30 },
      labels: { top: '40 u', right: '30 u', left: '30 u', bottom: '40 u', center: 'Área = ?' },
      highlightEdges: ['top', 'bottom', 'left', 'right'],
      unit: 'u',
    },
  });
}

export function demoGeometryCanvasTrianglePayload(
  subtype: 'right' | 'isosceles' | 'equilateral' | 'scalene' = 'scalene',
): GeometryCanvasPayload {
  const templates: Record<
    typeof subtype,
    Pick<GeometryCanvasPayload, 'title' | 'bodyText' | 'prompt' | 'formulaHint'> & {
      geometry: GeometryCanvasPayload['geometry'];
    }
  > = {
    right: {
      title: 'Triángulo rectángulo',
      bodyText: 'Identifica los catetos y calcula el área.',
      prompt: '¿Cuál es el área del triángulo?',
      formulaHint: 'Área = (base × altura) ÷ 2',
      geometry: {
        shapeType: 'triangle',
        triangleSubtype: 'right',
        dimensions: { base: 6, height: 8 },
        labels: { bottom: '6 cm', left: '8 cm', center: 'A = ?' },
        annotations: [
          { label: 'A', kind: 'vertex', at: 'v0' },
          { label: 'B', kind: 'vertex', at: 'v2' },
          { label: 'C', kind: 'vertex', at: 'v1' },
          { label: '90°', kind: 'angle', at: 'v0-angle' },
        ],
        unit: 'cm',
      },
    },
    isosceles: {
      title: 'Triángulo isósceles',
      bodyText: 'Observa la simetría de los lados iguales.',
      prompt: '¿Qué lados son congruentes?',
      geometry: {
        shapeType: 'triangle',
        triangleSubtype: 'isosceles',
        dimensions: { base: 10, height: 12 },
        labels: { bottom: '10 cm', left: '12 cm', right: '12 cm', center: 'A = ?' },
        annotations: [
          { label: 'A', kind: 'vertex', at: 'v2' },
          { label: 'B', kind: 'vertex', at: 'v0' },
          { label: 'C', kind: 'vertex', at: 'v1' },
        ],
        unit: 'cm',
      },
    },
    equilateral: {
      title: 'Triángulo equilátero',
      bodyText: 'Los tres lados miden lo mismo.',
      prompt: '¿Cuál es el perímetro?',
      geometry: {
        shapeType: 'triangle',
        triangleSubtype: 'equilateral',
        dimensions: { base: 8, height: 7 },
        labels: { bottom: '8 cm', left: '8 cm', right: '8 cm', center: 'P = ?' },
        annotations: [
          { label: 'A', kind: 'vertex', at: 'v2' },
          { label: 'B', kind: 'vertex', at: 'v0' },
          { label: 'C', kind: 'vertex', at: 'v1' },
        ],
        unit: 'cm',
      },
    },
    scalene: {
      title: 'Triángulo escaleno',
      bodyText: 'Ningún lado coincide en longitud.',
      prompt: '¿Cuál es el lado más largo?',
      geometry: {
        shapeType: 'triangle',
        triangleSubtype: 'scalene',
        dimensions: { base: 9, height: 11 },
        labels: { bottom: '9 cm', left: '11 cm', right: '13 cm', center: 'A = ?' },
        annotations: [
          { label: 'A', kind: 'vertex', at: 'v2' },
          { label: 'B', kind: 'vertex', at: 'v0' },
          { label: 'C', kind: 'vertex', at: 'v1' },
        ],
        unit: 'cm',
      },
    },
  };

  return validateGeometryCanvasPayload(templates[subtype]);
}
